import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  FiatCurrency,
  InGameCurrency,
  Rarity,
  RewardConfigRarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { missionBackground } from '@sorare/core/src/components/missions/style';
import { RewardUnlockedAnimation } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation';
import {
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_WHEEL,
  ANY_SPORT_SERIES_MARKET_COMMON_PACKS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useGetMyCardsBoostLink } from '@sorare/core/src/hooks/my-cards/useGetMyCardsBoostLink';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { fantasy, glossary, wheel } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  getRewardImage,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';
import { useIsSpecialCardSet } from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MonetaryRewardConversion } from './MonetaryRewardConversion/index.tsx';
import type {
  RewardUnlockedStory_rewardConfig,
  RewardUnlockedStory_taskInterface,
} from './__generated__/index.graphql.ts';
import { useOnCommonEssenceClick } from './useOnCommonEssenceClick.ts';

const Root = styled(Vertical).attrs({ center: true, gap: 4 })`
  position: relative;
  ${missionBackground}
  height: 100%;
  width: 100%;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-height: min(calc(90 * var(--unit)), 100vh);
  }
`;

const ButtonsWrapper = styled(Vertical)`
  position: absolute;
  bottom: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);

  opacity: 0;
  animation: ${fadeIn} 0.65s cubic-bezier(0.75, 0.07, 0.23, 0.89) forwards;
  animation-delay: 1.35s;
`;

const messages = defineMessages({
  clueTitle: {
    defaultMessage: "You've gained crafting clue!",
  },
  defaultTitle: {
    defaultMessage: "You've won a reward!",
  },
  xpTitle: {
    defaultMessage: "You've gained {rarity} XP!",
  },
  mixedXpTitle: {
    defaultMessage: "You've gained XP!",
  },
  xpSubtitle: {
    defaultMessage: 'Level up your players with XP.',
  },
  essenceTitle: {
    defaultMessage: "You've won {rarity} Essence!",
  },
  essenceSubtitle: {
    defaultMessage: 'Use Essence to craft new players.',
  },
  essenceSubtitleCommon: {
    defaultMessage: 'Use Essence to buy new packs.',
  },
  monetaryTitle: {
    defaultMessage: "You've won cash!",
  },
  marketCreditTitle: {
    defaultMessage: "You've won market credits!",
  },
  marketCreditSubTitle: {
    defaultMessage: 'Use them in Auctions or Instant Buy',
  },
  wheelTicketTitle: {
    defaultMessage:
      "You've won {rarity} {count, plural, one {wheel ticket} other {wheel tickets}}!",
  },
  wheelTicketSubtitle: {
    defaultMessage: 'Use them to spin the wheel.',
  },
  gemTitle: {
    defaultMessage: "You've won Gems!",
  },
});

type Props = {
  onClose: () => void;
  task: RewardUnlockedStory_taskInterface;
  alternativeRewardConfigs?: RewardUnlockedStory_rewardConfig[];
  showContinueButton?: boolean;
  action?: ReactNode;
  header?: ReactNode;
};

type DumbRewardUnlockedStoryProps = {
  header?: ReactNode;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: string;
  amount?: string;
  action?: ReactNode;
  rewardConfigs: RewardUnlockedStory_taskInterface['rewardConfigs'];
  rgbColor?: string;
  children?: ReactNode;
  showContinueButton?: boolean;
};

const DumbRewardUnlockedStory = ({
  header,
  onClose,
  title = <FormattedMessage {...messages.defaultTitle} />,
  subtitle,
  amount,
  action,
  rewardConfigs,
  rgbColor = 'var(--c-rgb-green-400)',
  children,
  showContinueButton = true,
}: DumbRewardUnlockedStoryProps) => {
  const isSpecialCardSetForReward = useIsSpecialCardSet();

  return (
    <Root
      style={
        {
          '--rgb-color': rgbColor,
        } as CSSProperties
      }
    >
      {header}
      <RewardUnlockedAnimation
        imgUrls={rewardConfigs.map(r =>
          getRewardImage(r, isSpecialCardSetForReward)
        )}
        title={title}
        subtitle={subtitle}
        amount={amount}
      >
        {children}
      </RewardUnlockedAnimation>
      <ButtonsWrapper>
        {action}
        {!!showContinueButton && (
          <Button
            size="large"
            onClick={() => {
              onClose?.();
            }}
            color={action ? 'transparent' : 'quaternary'}
            disableDebounce
          >
            <FormattedMessage {...glossary.continue} />
          </Button>
        )}
      </ButtonsWrapper>
    </Root>
  );
};

export const RewardUnlockedStory = ({
  onClose,
  task,
  alternativeRewardConfigs,
  showContinueButton = true,
  action: actionProp,
  header,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { sport, generateSportContextPath } = useSportContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { formatMessage } = useIntl();
  const isSpecialCardSet = useIsSpecialCardSet();
  const onCommonEssenceClick = useOnCommonEssenceClick(!showContinueButton);
  const rewardConfigs = alternativeRewardConfigs || task.rewardConfigs;

  const mergedRewardConfigs = mergeRewardsConfig(rewardConfigs);
  const firstRewardConfig = mergedRewardConfigs.at(0);

  if (!firstRewardConfig) {
    return null;
  }

  const rewardsGroupedByType = groupBy(rewardConfigs, r => r.__typename);

  const commonProps = {
    onClose,
    header,
    rewardConfigs,
  };

  // We only support multiple rewards with the same type for now
  if (Object.keys(rewardsGroupedByType).length === 1) {
    // TODO: Handle other summable types
    if (rewardsGroupedByType.InGameCurrencyRewardConfig) {
      const inGameCurrenciesGroupedByCurrency = groupBy(
        rewardsGroupedByType.InGameCurrencyRewardConfig,
        r => r.currency
      );
      const rewardCountsByCurrency = Object.entries(
        inGameCurrenciesGroupedByCurrency
      ).reduce<Record<InGameCurrency, number>>(
        (acc, [currency, rewards]) => {
          acc[currency as InGameCurrency] = rewards.reduce<number>(
            (sum, curr) => {
              return sum + curr.amount;
            },
            0
          );
          return acc;
        },
        {} as Record<InGameCurrency, number>
      );
      const rarityTyped =
        inGameCurrencyToRarity[
          Object.keys(rewardCountsByCurrency)[0] as InGameCurrency
        ];
      let amount;
      let title;
      let subtitle;
      let action;
      if (Object.keys(rewardCountsByCurrency).length === 1) {
        const currency = Object.keys(rewardCountsByCurrency).at(
          0
        ) as InGameCurrency;
        if (
          currency === InGameCurrency.COMMON_WHEEL_TICKET ||
          currency === InGameCurrency.WHEEL_TICKET
        ) {
          const rarity =
            currency === InGameCurrency.COMMON_WHEEL_TICKET
              ? Rarity.common
              : Rarity.limited;
          title = formatMessage(messages.wheelTicketTitle, {
            rarity: scarcityNames[rarity],
            count: Object.values(rewardCountsByCurrency).at(0),
          });
          subtitle = formatMessage(messages.wheelTicketSubtitle);
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}
              color="primary"
            >
              <FormattedMessage {...wheel.spin} />
            </Button>
          );
        } else if (
          currency === InGameCurrency.COUNTRY_CRAFT_CLUE ||
          currency === InGameCurrency.COMPETITION_CRAFT_CLUE ||
          currency === InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE
        ) {
          title = formatMessage(messages.clueTitle);
          subtitle = '';
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}
              color="primary"
            >
              <FormattedMessage {...wheel.spin} />
            </Button>
          );
        } else if (
          InGameCurrency.COMMON_XP === currency ||
          InGameCurrency.LIMITED_XP === currency ||
          InGameCurrency.RARE_XP === currency ||
          InGameCurrency.SUPER_RARE_XP === currency ||
          InGameCurrency.UNIQUE_XP === currency
        ) {
          title = formatMessage(messages.xpTitle, {
            rarity: scarcityNames[rarityTyped || Rarity.common],
          });
          subtitle = formatMessage(messages.xpSubtitle);
          action = (
            <Button
              size="large"
              to={getMyCardsBoostLink({
                rarity: rarityTyped || Rarity.common,
                sport,
              })}
              color="primary"
            >
              <FormattedMessage {...fantasy.levelUp} />
            </Button>
          );
        } else if (InGameCurrency.COMMON_GEM === currency) {
          title = formatMessage(messages.gemTitle);
          action = (
            <Button
              size="large"
              to={generateSportContextPath(
                ANY_SPORT_SERIES_MARKET_COMMON_PACKS
              )}
              color="primary"
            >
              <FormattedMessage {...glossary.goToMarket} />
            </Button>
          );
        } else {
          title = formatMessage(messages.defaultTitle);
        }
        amount = formatNumber(Object.values(rewardCountsByCurrency)[0]);
      } else {
        title = formatMessage(messages.mixedXpTitle);
        amount = formatNumber(
          Object.values(rewardCountsByCurrency).reduce(
            (acc, curr) => acc + curr,
            0
          )
        );
      }
      return (
        <DumbRewardUnlockedStory
          {...commonProps}
          title={title}
          amount={amount}
          subtitle={subtitle}
          rgbColor={
            Object.keys(rewardCountsByCurrency).length === 1 && rarityTyped
              ? getRarityRGBColor(rarityTyped)
              : undefined
          }
          action={actionProp || action}
        />
      );
    }
  }

  if (isType(firstRewardConfig, 'CardShardRewardConfig')) {
    const rarity = (
      firstRewardConfig.configRarity !== RewardConfigRarity.unknown_rarity
        ? firstRewardConfig.configRarity
        : Rarity.limited
    ) as Rarity;
    const isCommonPackEssence =
      firstRewardConfig.configRarity === Rarity.common &&
      sport !== Sport.BASEBALL;

    let action: ReactNode = (
      <Button
        to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
          params: { type: rarity.toLowerCase() },
        })}
        size="large"
        color="primary"
      >
        <FormattedMessage defaultMessage="Craft players" />
      </Button>
    );

    if (isCommonPackEssence) {
      action = (
        <LoadingButton
          size="large"
          color="primary"
          onClick={onCommonEssenceClick}
        >
          <FormattedMessage defaultMessage="Buy packs" />
        </LoadingButton>
      );
    }

    return (
      <>
        <DumbRewardUnlockedStory
          {...commonProps}
          title={formatMessage(messages.essenceTitle, {
            rarity: isSpecialCardSet(firstRewardConfig.cardSet?.slug)
              ? firstRewardConfig.cardSet?.name
              : scarcityNames[rarity],
          })}
          subtitle={formatMessage(
            isCommonPackEssence
              ? messages.essenceSubtitleCommon
              : messages.essenceSubtitle
          )}
          amount={formatNumber(firstRewardConfig.quantity)}
          rgbColor={getRarityRGBColor(rarity)}
          action={actionProp || action}
          showContinueButton={showContinueButton}
        />
      </>
    );
  }

  if (isType(firstRewardConfig, 'ConversionCreditRewardConfig')) {
    const { maxDiscount } = firstRewardConfig;
    const usdAmount = formatNumber((maxDiscount.usdCents || 0) / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
    return (
      <DumbRewardUnlockedStory
        {...commonProps}
        title={formatMessage(messages.marketCreditTitle)}
        subtitle={formatMessage(messages.marketCreditSubTitle)}
        amount={usdAmount}
        action={
          actionProp || (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY)}
              color="primary"
            >
              <FormattedMessage defaultMessage="Scout players" />
            </Button>
          )
        }
        rgbColor="29, 60, 55"
      />
    );
  }

  if (isType(firstRewardConfig, 'MonetaryRewardConfig')) {
    const monetaryAmount = toMonetaryAmount(firstRewardConfig.monetaryAmount);
    const totalCash = monetaryAmount.usd;

    const usdAmount =
      firstRewardConfig.sharedPool && firstRewardConfig.poolSize > 0
        ? totalCash / firstRewardConfig.poolSize
        : totalCash;

    const formattedTotalCash = formatNumber(usdAmount, {
      style: 'currency',
      currency: FiatCurrency.USD,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    });

    return (
      <DumbRewardUnlockedStory
        {...commonProps}
        title={formatMessage(messages.monetaryTitle)}
        amount={formattedTotalCash}
        rgbColor="49, 42, 99"
        action={actionProp}
      >
        <MonetaryRewardConversion usdAmount={usdAmount} />
      </DumbRewardUnlockedStory>
    );
  }

  return <DumbRewardUnlockedStory {...commonProps} action={actionProp} />;
};

const fragments = {
  rewardConfigs: gql`
    fragment RewardUnlockedStory_rewardConfig on AnyRewardConfigInterface {
      id
      ... on InGameCurrencyRewardConfig {
        id
        currency
        amount
      }
      ... on CardShardRewardConfig {
        id
        quantity
        configRarity
        cardSet {
          slug
          name
        }
      }
      ... on MonetaryRewardConfig {
        id
        poolSize
        sharedPool
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on ConversionCreditRewardConfig {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ...getRewardImage_anyRewardConfigInterface
      ...mergeRewardsConfig_anyRewardConfigInterface
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${getRewardImage.fragments.anyRewardConfigInterface}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardUnlockedStory_rewardConfig>,
};

RewardUnlockedStory.fragments = {
  taskInterface: gql`
    fragment RewardUnlockedStory_taskInterface on TaskInterface {
      id

      rewardConfigs {
        id
        ...RewardUnlockedStory_rewardConfig
      }
      alternativeRewardConfigs {
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
    }
    ${fragments.rewardConfigs}
  ` as TypedDocumentNode<RewardUnlockedStory_taskInterface>,
};
